import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { MapPin, Scale, Briefcase, MessageCircle, Sparkles } from 'lucide-react';
import { apiRequest } from '../utils/api';
import { useAuth } from '../components/auth/AuthComponents';
import UnauthorizedBox from '../components/shared/UnauthorizedBox';
import { canBrowseFindLawyer } from '../utils/userPermissions';

export default function FindALawyerPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [purpose, setPurpose] = useState('');
  const [preferredLocation, setPreferredLocation] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);
  const [hasSearched, setHasSearched] = useState(false);

  const handleMatch = async (e) => {
    e.preventDefault();
    if (!purpose.trim() || !preferredLocation.trim()) {
      setError('Please describe your legal need and preferred location.');
      return;
    }
    setLoading(true);
    setError('');
    setHasSearched(true);
    try {
      const json = await apiRequest('lawyers/match', {
        method: 'POST',
        body: {
          purpose: purpose.trim(),
          preferred_location: preferredLocation.trim(),
        },
      });
      setData(json);
    } catch (err) {
      setError(err.message || 'Failed to find matching lawyers');
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  if (!canBrowseFindLawyer(user?.role)) {
    return (
      <UnauthorizedBox
        title="Unauthorized"
        message="Find a Lawyer is not available for lawyer accounts."
        upgradeMessage=""
        showUpgrade={false}
        backToDashboard
      />
    );
  }

  return (
    <div className="min-h-full text-[var(--color-text)] bg-[var(--color-bg)]">
      <Helmet>
        <title>Find a Lawyer | DocxsAi</title>
        <meta name="description" content="Find verified lawyers matched to your legal need on DocxsAi." />
      </Helmet>

      <div className="absolute top-10 left-4 w-32 h-32 sm:top-20 sm:left-20 sm:w-80 sm:h-80 bg-gradient-to-br from-[var(--color-gradient-from)]/10 to-[var(--color-gradient-to)]/10 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute top-20 right-4 w-40 h-40 sm:top-40 sm:right-20 sm:w-96 sm:h-96 bg-gradient-to-br from-[var(--color-gradient-to)]/10 to-[var(--color-gradient-from)]/10 rounded-full blur-3xl pointer-events-none" />

      <section className="relative border-b border-[var(--color-border)]">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-[var(--color-primary-soft)] via-transparent to-transparent pointer-events-none" />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 pt-6 sm:pt-8 pb-8 sm:pb-10">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 rounded-full bg-[var(--color-primary-soft)] border border-[var(--color-primary)]/30 px-3 py-1 text-sm text-[var(--color-primary)] mb-4">
              <Scale className="w-4 h-4" />
              Find a Lawyer
            </div>
            <h1 className="text-3xl sm:text-4xl font-bold tracking-tight mb-4 text-[var(--color-text)]">
              Connect with{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[var(--color-gradient-from)] to-[var(--color-gradient-to)]">
                verified legal professionals
              </span>
            </h1>
            <p className="text-[var(--color-text-muted)] text-lg leading-relaxed">
              Describe your legal need and preferred location. We match you with approved lawyers whose specializations fit your purpose, ranked by proximity to you.
            </p>
          </div>
        </div>
      </section>

      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 py-8 pb-12">
        <form onSubmit={handleMatch} className="elevated-card rounded-2xl p-4 sm:p-6 mb-8 space-y-4">
          <div>
            <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-1.5">
              Purpose of requesting a lawyer <span className="text-[var(--color-danger)]">*</span>
            </label>
            <textarea
              value={purpose}
              onChange={(e) => setPurpose(e.target.value)}
              placeholder="e.g. I need help reviewing an employment contract before signing…"
              rows={4}
              className="themed-input w-full px-4 py-3 rounded-xl text-[var(--color-text)] placeholder-[var(--color-placeholder)] focus:outline-none focus:border-[var(--color-primary)] resize-y min-h-[100px]"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-1.5">
              Preferred location <span className="text-[var(--color-danger)]">*</span>
            </label>
            <input
              value={preferredLocation}
              onChange={(e) => setPreferredLocation(e.target.value)}
              placeholder="e.g. Lagos, Nigeria"
              className="themed-input w-full px-4 py-2.5 rounded-xl text-[var(--color-text)] placeholder-[var(--color-placeholder)] focus:outline-none focus:border-[var(--color-primary)]"
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            className="brand-gradient-btn inline-flex items-center gap-2 px-6 py-3 rounded-xl disabled:opacity-50 font-semibold"
          >
            <Sparkles className="w-4 h-4" />
            {loading ? 'Finding matches…' : 'Get Matching Lawyers'}
          </button>
        </form>

        {error ? (
          <div className="rounded-xl border border-red-500/30 bg-red-500/10 p-4 text-[var(--color-danger)] mb-6">{error}</div>
        ) : null}

        {loading ? (
          <div className="flex justify-center py-20">
            <div className="w-10 h-10 border-4 border-[var(--color-primary)] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : hasSearched && !loading ? (
          !data?.lawyers?.length ? (
            <div className="text-center py-20 rounded-2xl border border-dashed border-[var(--color-border)] elevated-card">
              <Briefcase className="w-12 h-12 text-[var(--color-text-muted)] mx-auto mb-4" />
              <p className="text-[var(--color-text-secondary)] font-medium mb-2">
                {data?.message || 'No lawyers matched your request yet'}
              </p>
              <p className="text-[var(--color-text-muted)] text-sm">Try describing your legal need in more detail or adjust your location.</p>
            </div>
          ) : (
            <>
              <p className="text-sm text-[var(--color-text-muted)] mb-4">
                {data.total} matching profile{data.total === 1 ? '' : 's'} — ranked nearest to {preferredLocation}
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                {data.lawyers.map((lawyer) => (
                  <div
                    key={lawyer.id}
                    className="elevated-card rounded-2xl p-5 hover:border-[var(--color-primary)]/40 transition-all flex flex-col"
                  >
                    <div className="flex items-start gap-4 flex-1">
                      <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[var(--color-gradient-from)] to-[var(--color-gradient-to)] flex items-center justify-center text-xl font-bold shrink-0 overflow-hidden text-[var(--color-text-inverse)]">
                        {lawyer.photo_url ? (
                          <img src={lawyer.photo_url} alt="" className="w-full h-full object-cover" />
                        ) : (
                          lawyer.display_name?.charAt(0)?.toUpperCase() || 'L'
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <h2 className="font-bold text-[var(--color-text)] truncate">{lawyer.display_name}</h2>
                        {lawyer.firm_name ? (
                          <p className="text-sm text-[var(--color-text-muted)] truncate">{lawyer.firm_name}</p>
                        ) : null}
                        <p className="text-sm text-[var(--color-text-secondary)] mt-2 line-clamp-2">{lawyer.headline}</p>
                        {(lawyer.city || lawyer.country) ? (
                          <p className="flex items-center gap-1 text-xs text-[var(--color-text-muted)] mt-3">
                            <MapPin className="w-3.5 h-3.5" />
                            {[lawyer.city, lawyer.country].filter(Boolean).join(', ')}
                          </p>
                        ) : null}
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-1.5 mt-4">
                      {(lawyer.practice_areas || []).slice(0, 3).map((area) => (
                        <span key={area} className="text-[11px] px-2 py-0.5 rounded-full bg-[var(--color-primary-soft)] text-[var(--color-primary)]">
                          {area}
                        </span>
                      ))}
                    </div>
                    <button
                      type="button"
                      onClick={() => navigate(`/lawyers/${lawyer.public_slug}`)}
                      className="brand-gradient-btn mt-4 inline-flex items-center justify-center gap-2 w-full px-4 py-2.5 rounded-xl text-sm font-semibold"
                    >
                      <MessageCircle className="w-4 h-4" />
                      Contact
                    </button>
                  </div>
                ))}
              </div>
            </>
          )
        ) : (
          <div className="text-center py-16 rounded-2xl border border-dashed border-[var(--color-border)] elevated-card">
            <Sparkles className="w-10 h-10 text-[var(--color-primary)] mx-auto mb-3" />
            <p className="text-[var(--color-text-secondary)] font-medium">Tell us what you need</p>
            <p className="text-[var(--color-text-muted)] text-sm mt-1 max-w-md mx-auto">
              Enter your legal purpose and preferred location above, then click Get Matching Lawyers to see verified profiles.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
